import type { ClassValue } from 'clsx';
import { useEffect, useMemo } from 'react';

import { new__cn as cn } from '../util';
import { SLIDER_TRACK_MARKER_SIZE } from './constant';
import { useVSliderContext } from './hook';
import type { VSliderNormalizedMarkerItem } from './type';
import {
  calculateSliderTrackWidth,
  createSliderValueUpdater,
  getClosestMarkerItem,
} from './util';

type VSliderTrack = {
  className?: ClassValue;
  trackMarkerClassName?: (value: number) => ClassValue;
  onTrackClick?(value: number): void;
};

export const VSliderTrack = ({
  className,
  children,
  trackMarkerClassName,
  ...rest
}: React.PropsWithChildren<VSliderTrack>) => {
  const {
    disabled,
    normalizedMarkerItemMap,
    normalizedValue,
    marker,
    max,
    min,
    readOnly,
    step,
    style,
    type,
    $track,
    setValue,
  } = useVSliderContext();

  const width = useMemo(
    () => calculateSliderTrackWidth(max, min, step),
    [max, min, step],
  );

  const changeValue = createSliderValueUpdater({
    normalizedValue,
    min,
    type,
    setValue,
  });

  const onTrackClick: React.MouseEventHandler<HTMLElement> = e => {
    if (disabled || readOnly || !normalizedMarkerItemMap) return;

    if (
      e.target instanceof HTMLElement &&
      e.target.ariaLabel === 'v-slider-track-marker' &&
      e.target.ariaValueText != null &&
      !isNaN(+e.target.ariaValueText)
    ) {
      onTrackMarkerClick(+e.target.ariaValueText);

      return;
    }

    const closestMarkerItem = getClosestMarkerItem({
      markerItemMap: normalizedMarkerItemMap,
      x: e.clientX - $track.current!.getBoundingClientRect().x,
    });

    if (typeof rest.onTrackClick === 'function') {
      rest.onTrackClick(closestMarkerItem.value);

      return;
    }

    changeValue(closestMarkerItem.value);
  };

  const onTrackMarkerClick = (value: number) => {
    if (disabled || readOnly) return;

    if (typeof rest.onTrackClick === 'function') {
      rest.onTrackClick(value);

      return;
    }

    changeValue(value);
  };

  useEffect(() => {
    if (!$track.current) return;

    $track.current.style.width = `${width}px`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  return (
    <div
      ref={$track}
      aria-disabled={disabled}
      aria-label="v-slider-track"
      aria-readonly={readOnly}
      className={cn(style.track, className)}
      onClick={onTrackClick}
    >
      {marker &&
        normalizedMarkerItemMap &&
        Object.entries(normalizedMarkerItemMap).map(([key, row]) => {
          if (row == null || row.hidden) return null;

          return (
            <VSliderTrackMarker
              key={key}
              className={trackMarkerClassName?.(row.value)}
              {...row}
            />
          );
        })}

      {children}
    </div>
  );
};

const VSliderTrackMarker = ({
  className,
  ...row
}: VSliderNormalizedMarkerItem & {
  className?: ClassValue;
}) => {
  const { disabled, readOnly } = useVSliderContext();

  return (
    <div
      aria-disabled={disabled}
      aria-label="v-slider-track-marker"
      aria-readonly={readOnly}
      aria-valuetext={`${row.value}`}
      className={cn(className)}
      style={{
        width: `${SLIDER_TRACK_MARKER_SIZE}px`,
        height: `${SLIDER_TRACK_MARKER_SIZE}px`,
        left: `${row.left.track}px`,
      }}
    />
  );
};

VSliderTrack.displayName = 'VSliderTrack';
VSliderTrackMarker.displayName = 'VSliderTrackMarker';
